import apiClient from '../../../../shared/api/apiClient'
import { ENDPOINTS } from '../../../../shared/api/endpoints'
import type { FeeReceiptTemplateConfig } from '../types/feeReceiptTemplate.types'

export const FEE_RECEIPT_TEMPLATE_KEY = 'FEE_RECEIPT_V1'

export const defaultFeeReceiptTemplate: FeeReceiptTemplateConfig = {
  schemaVersion: 1,
  templateName: 'Official School Fee Receipt',
  receiptLayout: 'minimal',
  institution: {
    schoolName: "ST. XAVIER'S GLOBAL SCHOOL",
    address: '12th Avenue, Knowledge Park III, Greater Noida',
    contactPhone: '+91 120 496 7890',
    email: 'accounts@stxaviersglobal.edu.in',
    logoUrl: null,
    showLogo: true,
    alignment: 'Center',
  },
  fields: {
    receiptNumber: true,
    paymentDate: true,
    studentDetails: true,
    classSection: true,
    parentDetails: true,
    paymentMode: true,
    feeBreakdownTable: true,
    totalAmountPaid: true,
  },
  receiptNumber: {
    prefix: 'REC-2026-',
    suffix: '',
  },
  termsAndConditions: 'This is a computer-generated fee receipt and does not require a physical signature.',

  // Legacy aliases for backward compatibility
  receiptFormat: {
    prefix: 'REC-2026-',
    suffix: '',
    dateFormat: 'DD-MMM-YYYY',
  },
  includedFields: {
    receiptNumber: true,
    paymentDate: true,
    studentDetails: true,
    classSection: true,
    parentDetails: true,
    paymentMode: true,
    feeBreakdownTable: true,
    totalAmountPaid: true,
  },
  typography: {
    fontFamily: 'Inter',
    fontSize: 'Medium',
    fontWeight: 'Bold',
    alignment: 'Center',
  },
  footer: {
    authorizedSignatoryLabel: 'Accounts Officer / Cashier',
    signatureUrl: '',
    termsAndConditions: 'This is a computer-generated fee receipt and does not require a physical signature.',
  },
}

export interface ResolvedFeeReceiptResult {
  templateId?: string
  templateKey: string
  config: FeeReceiptTemplateConfig
  isDefault?: boolean
}

export interface BackendFeeReceiptTemplatePayload {
  schemaVersion: number
  templateName: string
  receiptLayout: string
  institution: {
    schoolName: string
    address: string
    contactPhone: string
    email: string
    logoUrl: string | null
    showLogo: boolean
    alignment: string
  }
  fields: {
    receiptNumber: boolean
    paymentDate: boolean
    classSection: boolean
    feeBreakdownTable: boolean
    totalAmountPaid: boolean
    paymentMode: boolean
    studentDetails?: boolean
    parentDetails?: boolean
  }
  receiptNumber: {
    prefix: string
    suffix: string
  }
  termsAndConditions: string
}

export function serializeFeeReceiptTemplate(rawConfig: any): BackendFeeReceiptTemplatePayload {
  const config = typeof rawConfig === 'string' ? JSON.parse(rawConfig) : (rawConfig || {})

  const inst = config.institution || {}
  const fields = config.fields || config.includedFields || {}
  const rawPrefix = config.receiptNumber?.prefix
  const legacyPrefix = config.receiptFormat?.prefix
  const prefix =
    legacyPrefix && legacyPrefix !== 'REC-2026-'
      ? legacyPrefix
      : (rawPrefix ?? legacyPrefix ?? 'REC-2026-')

  const rawSuffix = config.receiptNumber?.suffix
  const legacySuffix = config.receiptFormat?.suffix
  const suffix =
    legacySuffix && legacySuffix !== ''
      ? legacySuffix
      : (rawSuffix ?? legacySuffix ?? '')
  const defaultTerms = 'This is a computer-generated fee receipt and does not require a physical signature.'
  const legacyTerms = config.footer?.termsAndConditions
  const rawTerms = config.termsAndConditions
  const terms =
    legacyTerms && legacyTerms !== defaultTerms
      ? legacyTerms
      : (rawTerms ?? legacyTerms ?? defaultTerms)

  return {
    schemaVersion: Number(config.schemaVersion || 1),
    templateName: String(config.templateName || 'Official School Fee Receipt'),
    receiptLayout: String(config.receiptLayout || 'minimal'),
    institution: {
      schoolName: inst.schoolName ?? '',
      address: inst.address ?? '',
      contactPhone: inst.contactPhone ?? '',
      email: inst.email ?? '',
      logoUrl: inst.logoUrl ?? null,
      showLogo: inst.showLogo !== false,
      alignment: inst.alignment ?? 'Center',
    },
    fields: {
      receiptNumber: fields.receiptNumber !== false,
      paymentDate: fields.paymentDate !== false,
      classSection: fields.classSection !== false,
      feeBreakdownTable: fields.feeBreakdownTable !== false,
      totalAmountPaid: fields.totalAmountPaid !== false,
      paymentMode: fields.paymentMode !== false,
      studentDetails: fields.studentDetails !== false,
      parentDetails: fields.parentDetails !== false,
    },
    receiptNumber: {
      prefix: String(prefix),
      suffix: String(suffix),
    },
    termsAndConditions: String(terms),
  }
}

/**
 * Safely parse content whether it is an object or a JSON string.
 */
export function parseFeeReceiptContent(rawContent: any): FeeReceiptTemplateConfig {
  if (!rawContent) return defaultFeeReceiptTemplate

  let parsed = rawContent
  if (typeof rawContent === 'string') {
    try {
      parsed = JSON.parse(rawContent)
    } catch {
      return defaultFeeReceiptTemplate
    }
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return defaultFeeReceiptTemplate
  }

  const instObj = {
    ...defaultFeeReceiptTemplate.institution,
    ...(parsed.institution || {}),
  }

  const fieldsObj = {
    receiptNumber: true,
    paymentDate: true,
    studentDetails: true,
    classSection: true,
    parentDetails: true,
    paymentMode: true,
    feeBreakdownTable: true,
    totalAmountPaid: true,
    ...(parsed.fields || parsed.includedFields || {}),
  }

  const receiptNumObj = {
    prefix: parsed.receiptNumber?.prefix ?? parsed.receiptFormat?.prefix ?? defaultFeeReceiptTemplate.receiptNumber?.prefix ?? 'REC-2026-',
    suffix: parsed.receiptNumber?.suffix ?? parsed.receiptFormat?.suffix ?? defaultFeeReceiptTemplate.receiptNumber?.suffix ?? '',
  }

  const termsStr =
    parsed.termsAndConditions ??
    parsed.footer?.termsAndConditions ??
    defaultFeeReceiptTemplate.termsAndConditions ??
    'This is a computer-generated fee receipt and does not require a physical signature.'

  return {
    schemaVersion: parsed.schemaVersion ?? 1,
    templateName: parsed.templateName ?? 'Official School Fee Receipt',
    receiptLayout: parsed.receiptLayout ?? 'minimal',
    institution: instObj,
    fields: fieldsObj,
    receiptNumber: receiptNumObj,
    termsAndConditions: termsStr,

    // Legacy aliases for backward compatibility
    receiptFormat: {
      prefix: receiptNumObj.prefix,
      suffix: receiptNumObj.suffix,
      dateFormat: parsed.receiptFormat?.dateFormat ?? 'DD-MMM-YYYY',
    },
    includedFields: fieldsObj,
    footer: {
      authorizedSignatoryLabel: parsed.footer?.authorizedSignatoryLabel ?? 'Accounts Officer / Cashier',
      signatureUrl: parsed.footer?.signatureUrl ?? '',
      termsAndConditions: termsStr,
    },
    typography: {
      fontFamily: parsed.typography?.fontFamily ?? 'Inter',
      fontSize: parsed.typography?.fontSize ?? 'Medium',
      fontWeight: parsed.typography?.fontWeight ?? 'Bold',
      alignment: parsed.institution?.alignment ?? parsed.typography?.alignment ?? 'Center',
    },
  }
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const isUuid = (val?: string | null): val is string => {
  return typeof val === 'string' && UUID_REGEX.test(val)
}

/**
 * GET /api/org-templates/resolved/FEE_RECEIPT_V1?branchId={branchId}
 */
export const fetchResolvedFeeReceiptTemplate = async (
  branchId?: string
): Promise<ResolvedFeeReceiptResult> => {
  try {
    const url = `${ENDPOINTS.ORG_TEMPLATES.RESOLVED(FEE_RECEIPT_TEMPLATE_KEY)}${
      branchId ? `?branchId=${encodeURIComponent(branchId)}` : ''
    }`
    const response = await apiClient.get<any>(url)
    const data = response.data?.data || response.data

    if (data && (data.content !== undefined || data.id)) {
      const mergedConfig = parseFeeReceiptContent(data.content)
      const rawId = data.id || data.orgTemplateId
      const templateId = data.isDefault ? undefined : (isUuid(rawId) ? rawId : undefined)
      return {
        templateId,
        templateKey: data.templateKey || FEE_RECEIPT_TEMPLATE_KEY,
        config: mergedConfig,
        isDefault: Boolean(data.isDefault),
      }
    }
  } catch (err: any) {
    if (err?.response?.status === 404) {
      console.info(`No fee receipt template configured yet for key "${FEE_RECEIPT_TEMPLATE_KEY}". Returning default configuration.`)
    } else {
      console.error('Failed to load resolved fee receipt template:', err)
    }
  }

  return {
    templateId: undefined,
    templateKey: FEE_RECEIPT_TEMPLATE_KEY,
    config: defaultFeeReceiptTemplate,
    isDefault: true,
  }
}

/**
 * Backward-compatible fetch
 */
export const fetchFeeReceiptTemplate = async (templateId?: string): Promise<FeeReceiptTemplateConfig> => {
  const res = await fetchResolvedFeeReceiptTemplate()
  return res.config
}

/**
 * GET /api/org-templates?templateKey=FEE_RECEIPT_V1
 */
export const fetchOrgFeeReceiptTemplates = async (
  templateKey: string = FEE_RECEIPT_TEMPLATE_KEY
): Promise<any[]> => {
  try {
    const response = await apiClient.get<any>(
      `${ENDPOINTS.ORG_TEMPLATES.BASE}?templateKey=${encodeURIComponent(templateKey)}`
    )
    const data = response.data?.data || response.data
    return Array.isArray(data) ? data : []
  } catch {
    return []
  }
}

/**
 * POST /api/org-templates (Create) or PATCH /api/org-templates/:id (Update)
 */
export const saveFeeReceiptTemplate = async (
  config: FeeReceiptTemplateConfig,
  existingTemplateId?: string,
  branchId?: string
): Promise<{ success: boolean; data?: any; templateId?: string; message: string }> => {
  const payloadObject = serializeFeeReceiptTemplate(config)
  const jsonContentString = JSON.stringify(payloadObject)

  const hasValidExistingUuid = isUuid(existingTemplateId)

  if (hasValidExistingUuid && existingTemplateId) {
    const updatePayload = {
      content: jsonContentString,
      isActive: true,
    }
    const response = await apiClient.patch(
      ENDPOINTS.ORG_TEMPLATES.BY_ID(existingTemplateId),
      updatePayload
    )
    return {
      success: true,
      data: response.data,
      templateId: existingTemplateId,
      message: 'Fee Receipt Template updated successfully!',
    }
  } else {
    const createPayload = {
      templateKey: FEE_RECEIPT_TEMPLATE_KEY,
      branchId: branchId || undefined,
      content: jsonContentString,
      isActive: true,
    }
    const response = await apiClient.post<any>(
      ENDPOINTS.ORG_TEMPLATES.BASE,
      createPayload
    )
    const newId = response.data?.id || response.data?.data?.id
    const validatedNewId = isUuid(newId) ? newId : undefined
    return {
      success: true,
      data: response.data,
      templateId: validatedNewId,
      message: 'Fee Receipt Template created successfully!',
    }
  }
}

/**
 * DELETE /api/org-templates/:id
 */
export const deleteFeeReceiptTemplate = async (id: string): Promise<void> => {
  if (!isUuid(id)) {
    console.warn(`Attempted to delete fee receipt template with invalid UUID: "${id}". Skipping request.`)
    return
  }
  await apiClient.delete(ENDPOINTS.ORG_TEMPLATES.BY_ID(id))
}

export const saveFeeReceiptTemplateDraft = async (
  config: FeeReceiptTemplateConfig,
  templateId?: string
): Promise<{ success: boolean; message: string }> => {
  const res = await saveFeeReceiptTemplate(config, templateId)
  return { success: true, message: res.message }
}

export const publishFeeReceiptTemplate = async (
  config: FeeReceiptTemplateConfig,
  templateId?: string
): Promise<{ success: boolean; message: string }> => {
  const res = await saveFeeReceiptTemplate(config, templateId)
  return { success: true, message: res.message }
}
